import { useEffect, useLayoutEffect, useMemo } from "react"

import { datadogRum } from "@datadog/browser-rum"
import Intercom from "@intercom/messenger-js-sdk"
import { UserbackProvider } from "@userback/react"
import { App as AntdApp, ConfigProvider, Layout, notification, Spin, Typography } from "antd"
import enGB from "antd/locale/en_GB"
import cls from "classname"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useResizeDetector } from "react-resize-detector"
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { compose } from "redux"
import localStorage from "redux-persist/es/storage"

import { HelpModal } from "../../Components/Modal/HelpModal"
import PrivateRoute from "../../Components/PrivateRoute"
import Spinner from "../../Components/Spinner"
import withAuth from "../../hoc/withAuth"
// HOC
import useWindowSize from "../../hooks/use-window-size"
import { useAttributionTracking } from "../../hooks/useAttributionTracking"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import i18n from "../../i18n"
import ErrorsActions from "../../Redux/ErrorsRedux"
import NavActions from "../../Redux/NavRedux"
import StartupCreators from "../../Redux/StartupRedux"
import { defaultInnerPage } from "../../Sagas/StartupSagas"
import { darkTheme, lightTheme } from "../../theme"
import { loadInvalidTokenState, saveInvalidTokenState } from "../../Utils/invalidTokenStorage"
import {
  createAccountStepPath,
  dateFormat,
  excludeReferences,
  useBackListener
} from "../../Utils/utils"
import AddCard from "../AddCard"
import AdminAcceptInvitePage from "../AdminAcceptInvitePage"
import AdminTwoFactorSetupPage from "../AdminTwoFactorSetupPage"
import AdminTwoFactorVerificationPage from "../AdminTwoFactorVerificationPage"
import BusinessAccountsActions from "../BusinessAccountsPage/reducer"
import CheckEmailCode from "../CheckEmailCode"
import CheckMailboxPage from "../CheckMailboxPage"
import LayoutOnboarding from "../LayoutOnboarding/LayoutOnboarding"
import LoginPage from "../LoginPage"
import AuthActions from "../LoginPage/reducer"
import MCPConsentPage from "../MCPConsentPage"
import NewPasswordAdminPage from "../NewPasswordAdminPage"
import NewPasswordPage from "../NewPasswordPage"
import OAuthAuthPage from "../OAuthAuthPage"
import OnboardingAlertsPageNew from "../Onboarding/AlertsPage"
import OnboardingBusinessInfoPageNew from "../Onboarding/BusinessInfoPage"
import OnboardingCardInfoPageNew from "../Onboarding/CardInfoPage"
import OnboardingEnrollmentPageNew from "../Onboarding/EnrollmentPage"
import OnboardingPaymentConnectPageNew from "../Onboarding/PaymentConnectPage"
import OnboardingTopUp from "../Onboarding/TopUp"
import OnboardingAlertsPage from "../OnboardingAlertsPage"
import OnboardingPaymentConnectPage from "../OnboardingPaymentConnectPage"
import OnboardingQuestionsPage from "../OnboardingQuestionsPage"
import McpDocPage from "../PersonalSettings/Mcp/mcp-doc"
import PremiumFeaturesPage from "../PremiumFeaturesPage"
import RestorePasswordPage from "../RestorePasswordPage"
import ShopifyAuthPage from "../ShopifyAuthPage"
import SignUpPage from "../SignUpPage"
import StartupPage from "../StartupPage"
import StripeAuthPage from "../StripeAuthPage"
// REDUCERS
import UserActions from "../User/reducer"
import VerifyCodePage from "../VerifyCodePage"
import InternalPages from "./InternalPages"

const APP_TYPE = process.env.REACT_APP_TYPE

const myTheme = { light: lightTheme, dark: darkTheme }

const { Text } = Typography

dayjs.extend(utc)

const updateDataDogUser = (userInfo, isAdmin) => {
  if (!userInfo) return

  datadogRum.setUser({
    id: userInfo.id,
    name: userInfo.first_name,
    email: userInfo.email,
    current_business_account_id: userInfo.current_business_account_id,
    business_account_role: userInfo.ba_role?.name || userInfo.ba_role?.id || "No role",
    business_account_role_id: userInfo.ba_role?.id,
    is_admin: isAdmin,
    onboarding_finished: userInfo?.new_onboarding_finished || userInfo?.onboarding_finished
  })
}

//const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID
function Routess(props) {
  const {
    auth,
    theme,
    navSetProp,
    userInfo,
    isAdmin,
    isMobile,
    error,
    verifyToken,
    startup,
    dispatch,
    businessAccount,
    nextCreateAccountStep,
    redirectAfterCreateAccount,
    test_data
    //isPasswordCreated,
  } = props
  const { t } = useTranslation()

  const configNotification = {
    placement: isMobile ? "topRight" : "bottomRight",
    getContainer: () => document.getElementById("main-layout")
    //duration: 0,
  }

  const [apiNotification, contextHolder] = notification.useNotification(configNotification)
  const { width, ref } = useResizeDetector()
  const [, innerHeight] = useWindowSize()
  const location = useLocation()
  const navigate = useNavigate()
  const externalDemoMode = useExternalDemoMode()
  const search = location.search
  const stripe = useMemo(
    () => props.paymentProcessors.find((p) => p.service_name === "stripe"),
    [props.paymentProcessors]
  )
  const invalid = !!stripe?.has_invalid_token
  const [searchParams, setSearchParams] = useSearchParams()
  const inviteId = searchParams.get("invite")

  let isAuthUserPages = !!auth?.accessToken && !isAdmin
  let isAuthUserOnboardingPages =
    !!auth?.accessToken && !isAdmin && !userInfo?.is_created_by_invitation
  const onboarding_finish_notification = window.localStorage.getItem(
    "onboarding_finish_notification"
  )
  const isOnboardingPageAllowed =
    !userInfo?.new_onboarding_finished || onboarding_finish_notification === "true"
  const isVerifiedEmailOnboardingAllowed =
    isAuthUserOnboardingPages && isOnboardingPageAllowed && !!userInfo?.is_email_verified

  const isInvitedWithoutRole = userInfo?.is_created_by_invitation && !!userInfo?.role

  const setHelpModal = () => {
    if (isAdmin) return
    const params = new URLSearchParams(location.search)
    params.set("help_modal", "stripe_token_invalid")

    navigate({
      pathname: location.pathname,
      search: params.toString()
    })
  }

  // STARTUP
  useEffect(() => {
    dispatch(StartupCreators.startup(location))

    if (externalDemoMode) {
      if (!auth?.accessToken) {
        props.signIn({ email: "demo_mode@example.com", password: "12345678Xvz" })
      }
      if (theme === "dark") {
        props.dispatch(NavActions.navSetProp("theme", "light"))
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  useEffect(() => {
    const state = loadInvalidTokenState()
    const dismissed = !!state.dismissed
    const shown = !!state.shown

    if (!invalid) {
      if (shown || dismissed) saveInvalidTokenState({ shown: false, dismissed: false })
      return
    }

    if (shown || dismissed) return

    saveInvalidTokenState({ shown: true, dismissed: false })
    setHelpModal(true)
  }, [invalid])

  // PAGE VISIBILITY - RELOAD ON TAB FOCUS IF NEXT CREATE ACCOUNT STEP IS CHANGED
  useEffect(() => {
    let wasHidden = false
    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasHidden = true
      } else if (wasHidden) {
        try {
          /**
           * Load the persisted state from localStorage.
           * If the nextCreateAccountStep in the persisted state is different from the current one,
           * reload the page.
           */
          const persistedState = localStorage.getItem("persist:chargeback")
          if (persistedState) {
            const newState = JSON.parse(persistedState)
            const userState = newState.user ? JSON.parse(newState.user) : null
            const storedNextStep = userState?.nextCreateAccountStep

            if (storedNextStep !== undefined && storedNextStep !== nextCreateAccountStep) {
              if (storedNextStep !== nextCreateAccountStep) {
                window.location.reload()
              }
            }
          }
        } catch (error) {
          console.error("Error syncing storage on visibility change:", error)
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [nextCreateAccountStep])

  // for clear charts custom tooltip element from body of page (because it can not remove on other pages automatically)
  useEffect(() => {
    const element = document.getElementById("chartjs-tooltip")
    if (element) element?.remove()
    if (!isAdmin && userInfo) window?.Intercom?.("update")
  }, [location, isAdmin, userInfo])

  useEffect(() => {
    updateDataDogUser(userInfo, isAdmin)
    if (userInfo) datadogRum?.getUser()
  }, [userInfo, isAdmin])

  useEffect(() => {
    if (isAdmin) {
      props.getStatistics({ test_data })
    }
  }, [isAdmin, location.pathname, test_data])

  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      .setAttribute("content", theme === "dark" ? "#030625" : "#ffffff")
    document.body.style.backgroundColor = theme === "dark" ? "#030625" : "#ffffff"
  }, [theme])

  useEffect(() => {
    if (innerHeight) document.documentElement.style.setProperty("--app-height", `${innerHeight}px`)
  }, [innerHeight])

  // REFRESH TOKEN
  useEffect(() => {
    // if (auth?.accessToken) verifyToken(auth.accessToken)
    if (userInfo && !isAdmin) {
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        user_id: userInfo?.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
        name: userInfo?.first_name + " " + userInfo?.first_name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
        email: userInfo?.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
        created_at: dayjs(userInfo?.created).unix(), // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
        hide_default_launcher: true
      })
      if (userInfo?.["current_business_account_id"]) {
        const currentBusinessAccountId = userInfo?.["current_business_account_id"]

        if (nextCreateAccountStep && location.pathname?.includes("create-account")) {
          if (businessAccount?.id !== currentBusinessAccountId) {
            props.getBusinessAccount(currentBusinessAccountId)
          }
          if (!props.enrollments.length) props.getEnrollments(currentBusinessAccountId)
        } else {
          if (location.pathname?.includes("onboarding") || location.pathname === "/login") {
            if (businessAccount?.id !== currentBusinessAccountId && !inviteId) {
              props.getBusinessAccount(currentBusinessAccountId)
            }
            if (!props.enrollments.length) props.getEnrollments(currentBusinessAccountId)
            if (!props.paymentProcessors.length)
              props.getPaymentProcessors({
                merchant_id: currentBusinessAccountId,
                return_url: process.env.REACT_APP_BASE_URI + location.pathname
              })
          }
        }
      }
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [auth?.accessToken, auth?.refreshToken, userInfo?.id, isAdmin])

  useEffect(() => {
    if (userInfo && userInfo?.["current_business_account_id"]) {
      if (location.pathname?.includes("onboarding")) {
        if (businessAccount?.id !== userInfo?.["current_business_account_id"] && !inviteId)
          props.getBusinessAccount(userInfo?.["current_business_account_id"])
      }
    }
  }, [
    auth?.accessToken,
    auth?.refreshToken,
    userInfo?.["current_business_account_id"],
    location.pathname
  ])

  const user_data = useMemo(
    () => ({
      id: userInfo?.id,
      info: {
        name: userInfo?.first_name,
        email: userInfo?.email
      }
    }),
    [userInfo?.id]
  )

  // ERRORS
  useEffect(() => {
    if (error) {
      if (error?.message) {
        if (typeof error.message === "object") {
          for (const key in error.message) {
            //notification(`${key}: ${error.message[i]}`, `error_${key}`)
            if (key === "type") continue //if changed password there are more keys
            if (key === "detail" && typeof error.message[key] !== "object") {
              apiNotification.error({
                closeIcon: <Text>{t("Dismiss")}</Text>,
                message: error?.message[key],
                key: `error_${key}`
              })
            } else {
              apiNotification.error({
                closeIcon: <Text>{t("Dismiss")}</Text>,
                message: "Some back error, see response"
              })
            }
          }
        } else {
          apiNotification.error({
            closeIcon: <Text>{t("Dismiss")}</Text>,
            message: error?.message,
            key: "error notification"
          })
        }
      } else if (typeof error === "string") {
        apiNotification.error({
          closeIcon: <Text>{t("Dismiss")}</Text>,
          message: error,
          key: "error notification"
        })
      } else {
        for (const i in error) {
          if (i === "detail" && typeof error[i] !== "object") {
            apiNotification.error({
              closeIcon: <Text>{t("Dismiss")}</Text>,
              description: error[i],
              key: `error_${i}`
            })
          } else {
            apiNotification.error({
              closeIcon: <Text>{t("Dismiss")}</Text>,
              message: i,
              description: error[i],
              key: `error_${i}`
            })
          }
        }
      }
      props.errorReset()
    }
  }, [!!error])

  useEffect(() => {
    if (auth?.message) {
      apiNotification.success({
        closeIcon: <Text>{t("Dismiss")}</Text>,
        message: auth?.message,
        key: "success notification"
      })
      props.clearAuthMessage()
    }
  }, [auth?.message])

  // RESIZE
  useEffect(() => {
    const correctWidth = width || window.innerWidth
    if (correctWidth > 768) {
      navSetProp("isDesktop", true)
      navSetProp("isMobile", false)
    } else {
      navSetProp("isDesktop", false)
      navSetProp("isMobile", true)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [width, !!userInfo])

  // SET LANGUAGE
  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || "en")
    dayjs.locale(userInfo?.language === "zh-hans" ? "zh-cn" : userInfo?.language || "en")
  }, [userInfo?.language])

  useBackListener(({ location: backLocation }) => {
    if (
      excludeReferences.includes(backLocation.pathname) &&
      !excludeReferences.includes(location.pathname)
    ) {
      props.userSaveResultReset()
      props.getBusinessAccounts()
      navigate(createAccountStepPath[1] + (search || ""), { replace: true })
    } else if (
      excludeReferences.includes(backLocation.pathname) &&
      excludeReferences.includes(location?.pathname) &&
      !nextCreateAccountStep
    ) {
      navigate(defaultInnerPage, { replace: true })
    }
  })

  useLayoutEffect(() => {
    if (userInfo?.next_step >= 4 && !!userInfo && !isAdmin) {
      props.getBusinessAccountsWithRole()
      props.getBusinessAccounts()
    }
    if (isInvitedWithoutRole) {
      props.getBusinessAccounts()
    }
  }, [])

  // Attribution tracking
  useAttributionTracking()

  if (startup?.success === false) {
    return (
      <Layout>
        <Routes>
          <Route path="*" element={<StartupPage />} />
        </Routes>
      </Layout>
    )
  }

  Spin.setDefaultIndicator(<Spinner />)

  const appContent = (
    <ConfigProvider locale={enGB} dateFormat={dateFormat} theme={{ ...myTheme[theme] }}>
      <AntdApp notification={configNotification}>
        <Layout
          id="main-layout"
          ref={ref}
          className={cls("main-layout", {
            "theme-light": theme === "light",
            "theme-dark": theme === "dark"
          })}
        >
          {contextHolder}
          {APP_TYPE === "partner" ? (
            <Routes>
              <Route path="/" element={<SignUpPage isReferralOutsider />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/check-mailbox" element={<CheckMailboxPage />} />
              <Route path="/restore-password" element={<RestorePasswordPage />} />
              <Route path="/restore-password/:hash" element={<NewPasswordPage />} />
              <Route path="/restore-password/setting/:hash" element={<NewPasswordPage />} />
              <Route path="/verify-code" element={<VerifyCodePage />} />
              <Route path="/sign-up/confirm/:hash" element={<CheckEmailCode />} />
              <Route path="*" element={<InternalPages />} />
              <Route
                path="/onboarding/questions"
                element={
                  <PrivateRoute redirectPath={"/referrals"} auth={isAuthUserOnboardingPages}>
                    <OnboardingQuestionsPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<StartupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/:hash" element={<LoginPage />} />
              <Route
                path="/admin/2fa-verification/:token"
                element={<AdminTwoFactorVerificationPage />}
              />
              <Route path="/admin/2fa-setup/:token" element={<AdminTwoFactorSetupPage />} />
              <Route path="/admin-invite/:token" element={<AdminAcceptInvitePage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/sign-up/:hash" element={<SignUpPage />} />
              <Route path="/add-email" element={<SignUpPage />} />
              <Route path="/auth/shopify/" element={<ShopifyAuthPage />} />
              <Route path="/auth/stripe/" element={<StripeAuthPage />} />
              <Route
                path="/auth/nextcommerce/"
                element={<OAuthAuthPage provider="nextcommerce" />}
              />
              <Route path="/oauth/mcp/authorize" element={<MCPConsentPage />} />
              <Route path="/check-mailbox" element={<CheckMailboxPage />} />
              {/*_ONBOARDING PAGES_____START_*/}
              {/* <Route path="/onboarding/questions" element={
                <PrivateRoute redirectPath={'/dashboard'} auth={isAuthUserOnboardingPages}>
                  <OnboardingQuestionsPage/>
                </PrivateRoute>
              }/> //step 2.5 */}
              <Route element={<LayoutOnboarding />}>
                <Route
                  path="/onboarding/business-info"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingBusinessInfoPageNew step={1} maxStep={5} />
                      {/* <OnboardingBusinessInfoPage step={1} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/payment-connect"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingPaymentConnectPageNew step={2} maxStep={5} />
                      {/* <OnboardingPaymentConnectPage step={2} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/enrollment"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingEnrollmentPageNew step={3} maxStep={5} />
                      {/* <OnboardingEnrollmentPage step={3} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/alerts"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingAlertsPageNew step={4} maxStep={5} />
                      {/* <OnboardingAlertsPage step={4} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/card-info/:status/"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/card-info"
                  element={
                    // <PrivateRoute redirectPath={'/dashboard'} auth={isAuthUserOnboardingPages}>
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/top-up"
                  element={
                    // <PrivateRoute redirectPath={'/dashboard'} auth={isAuthUserOnboardingPages}>
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingTopUp step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
              </Route>
              {/*_ONBOARDING PAGES_____END_*/}
              {/*_CREATE BUSINESS ACCOUNT PAGES_____START_*/}
              <Route element={<LayoutOnboarding />}>
                <Route
                  path="/create-account/business-info"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingBusinessInfoPage step={1} maxStep={5}/> */}
                      <OnboardingBusinessInfoPageNew step={1} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/payment-connect"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingPaymentConnectPage step={2} maxStep={5}/> */}
                      <OnboardingPaymentConnectPageNew step={2} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/enrollment"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingEnrollmentPage step={3} maxStep={5}/> */}
                      <OnboardingEnrollmentPageNew step={3} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/alerts"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingAlertsPage step={4} maxStep={5}/> */}
                      <OnboardingAlertsPageNew step={4} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/card-info/:status/"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/card-info"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/top-up"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || "/dashboard"}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingTopUp step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
              </Route>
              {/*_CREATE BUSINESS PAGES_____END_*/}
              {/*_MANAGE ENROLLMENT PAGES_____START_*/}
              <Route
                path="/configure/enrollment/:enrollmentId/payment-connect"
                element={<OnboardingPaymentConnectPage step={1} maxStep={2} />}
              />
              <Route
                path="/configure/enrollment/:enrollmentId/alerts"
                element={<OnboardingAlertsPage step={2} maxStep={2} />}
              />
              <Route
                path="/business_settings/enrollment/:enrollmentId/payment-connect"
                element={<OnboardingPaymentConnectPage step={1} maxStep={2} />}
              />
              <Route
                path="/business_settings/enrollment/:enrollmentId/alerts"
                element={<OnboardingAlertsPage step={2} maxStep={2} />}
              />
              {/*_MANAGE ENROLLMENT PAGES_____END_*/}
              <Route path="/sign-up/confirm/:hash" element={<CheckEmailCode />} />
              <Route path="/success/:hash" element={<CheckEmailCode />} />
              <Route path="/api/success/:hash" element={<CheckEmailCode />} />
              <Route path="/restore-password" element={<RestorePasswordPage />} />
              <Route path="/verify-code" element={<VerifyCodePage />} />
              <Route path="/restore-password/:hash" element={<NewPasswordPage />} />
              <Route path="/restore-password/setting/:hash" element={<NewPasswordPage />} />
              <Route path="/create-new-password" element={<NewPasswordAdminPage />} />
              <Route
                path="integrations/premium_features"
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <PremiumFeaturesPage />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<InternalPages />} />
              <Route
                path="/add-card"
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <AddCard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/manual-top-up"
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <AddCard />
                  </PrivateRoute>
                }
              />
              <Route path="mcp" element={<McpDocPage />} />
            </Routes>
          )}
          <HelpModal />
        </Layout>
      </AntdApp>
    </ConfigProvider>
  )

  return !isMobile ? (
    <UserbackProvider token="A-hM9QRGQUnjNFvEdLoVb8XV1CN" options={{ user_data }}>
      {appContent}
    </UserbackProvider>
  ) : (
    appContent
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  theme: state.nav.theme,
  userInfo: state.auth.userInfo,
  isPasswordCreated: state.auth.isPasswordCreated,
  startup: state.startup,
  isDesktop: state.nav.isDesktop,
  isMobile: state.nav.isMobile,
  isAdmin: state.auth.isAdmin,
  error: state.errors.data,

  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount,
  enrollments: state.user.enrollments,
  paymentProcessors: state.user.paymentProcessors,
  nextCreateAccountStep: state.user.nextCreateAccountStep,
  redirectAfterCreateAccount: state.user.redirectAfterCreateAccount,

  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  navSetProp: (key, value) => dispatch(NavActions.navSetProp(key, value)),
  errorReset: () => dispatch(ErrorsActions.errorReset()),
  errorSave: (data) => dispatch(ErrorsActions.errorSave(data)),
  refreshToken: (token) => dispatch(AuthActions.refreshTokenRequest({ token })),
  refreshTokenFailure: (data) => dispatch(AuthActions.refreshTokenFailure(data)),
  logout: () => dispatch(AuthActions.logout()),
  signIn: (data) => dispatch(AuthActions.signInRequest(data)),
  verifyToken: (token) => dispatch(AuthActions.verifyTokenRequest({ token })),
  clearAuthMessage: () => dispatch(AuthActions.clearAuthMessage()),
  userSaveResultReset: () => dispatch(UserActions.userSaveResultReset()),
  changeNextCreateAccountStep: (nextStep) =>
    dispatch(UserActions.changeNextCreateAccountStep(nextStep)),
  getBusinessAccounts: () => dispatch(UserActions.getBusinessAccountsRequest()),
  getBusinessAccount: (businessAccountId) =>
    dispatch(UserActions.getBusinessAccountRequest(businessAccountId)),
  getEnrollments: (businessAccountId) =>
    dispatch(UserActions.getEnrollmentsRequest(businessAccountId)),
  getPaymentProcessors: (data) => dispatch(UserActions.getPaymentProcessorsRequest(data)),
  getBusinessAccountsWithRole: () => dispatch(UserActions.getBusinessAccountsWithRoleRequest()),

  getStatistics: (params) => dispatch(BusinessAccountsActions.getStatisticsAdminRequest(params)),
  dispatch
})

Routes.propTypes = {
  auth: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    isValidRefreshToken: PropTypes.bool
  }),
  userInfo: PropTypes.shape({
    onboarding_finished: PropTypes.bool,
    language: PropTypes.string
  }),
  startup: PropTypes.shape({
    success: PropTypes.bool
  }),
  isDesktop: PropTypes.bool,
  isMobile: PropTypes.bool,
  isAdmin: PropTypes.bool,
  error: PropTypes.any,
  dispatch: PropTypes.func
}

export default connect(mapStateToProps, mapDispatchToProps)(compose(withAuth)(Routess))
